import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { catchError, forkJoin, of } from "rxjs";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  RangoPeriodo,
  buildDonutStyle,
  normalizarGraficas,
  obtenerRangoPeriodo,
  porcentaje,
  toNumber,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PeriodoDashboardComponent } from "src/app/dashboard/shared/periodo-dashboard/periodo-dashboard.component";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { ProyectoDesviacion, ResumenDashboardProyectos } from "src/app/dashboard/pestanas/proyectos/proyectos-dashboard.model";
import { ProyectosDashboardService } from "src/app/dashboard/pestanas/proyectos/proyectos-dashboard.service";
import { Graficas } from "src/app/graficas/gestionGraficas/graficas.model";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";

/**
 * Pestaña "Proyectos" del Dashboard de Inicio: KPIs de hoy (activos,
 * retrasados, sin avance, avance real vs planeado, horas, riesgos), dona
 * por estatus, activos por cliente, creados por mes (unica grafica atada
 * al periodo), riesgos abiertos por severidad y el ranking de mayor
 * desviacion. Todo sale de un solo endpoint (controller/proyectos/dashboard.do)
 * con los conteos de TODO el tenant. Consulta al montarse; el cascaron
 * (dashini) la vuelve a consultar con actualizar().
 */
@Component({
  selector: "app-dashboard-proyectos",
  standalone: true,
  templateUrl: "./dashboard-proyectos.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css", "./dashboard-proyectos.component.css"],
  imports: [CommonModule, MatIcon, PeriodoDashboardComponent],
})
export class DashboardProyectosComponent implements OnInit, PestanaDashboard {
  /** Creados por mes con 30 dias serian una o dos barras: el anio da la tendencia. */
  readonly periodoInicial = "anio";

  errorCarga = "";
  kpis: DashboardKpi[] = [];
  estatusItems: DashboardGraphItem[] = [];
  clienteItems: DashboardGraphItem[] = [];
  mesItems: DashboardGraphItem[] = [];
  severidadItems: DashboardGraphItem[] = [];
  desviaciones: ProyectoDesviacion[] = [];
  totalProyectos = 0;
  activos = 0;
  totalRiesgos = 0;
  mesMax = 0;
  canViewGestion = false;
  canViewSeguimiento = false;

  private rango: RangoPeriodo = obtenerRangoPeriodo(this.periodoInicial);
  private cargandoDatos = false;
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  private readonly statusColors: Record<string, string> = {
    ACTIVO: "#00897b",
    PAUSADO: "#ef6c00",
    CERRADO: "#455a64",
    "SIN ESTATUS": "#8d8d8d",
  };

  /**
   * Las claves son las descripciones REALES de cos_severidad_riesgo en
   * mayusculas ("Baja", "Media", "Alta", "Crítico"), con y sin acento y
   * con la forma masculina por si el catalogo se edita: si una no empata,
   * el color cae al de la posicion y el semaforo se invierte.
   */
  private readonly severityColors: Record<string, string> = {
    CRITICO: "#d32f2f",
    "CRÍTICO": "#d32f2f",
    CRITICA: "#d32f2f",
    "CRÍTICA": "#d32f2f",
    ALTA: "#ef6c00",
    ALTO: "#ef6c00",
    MEDIA: "#1565c0",
    MEDIO: "#1565c0",
    BAJA: "#00897b",
    BAJO: "#00897b",
  };

  constructor(
    private proyectosDashboardService: ProyectosDashboardService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    const menus = this.authService.getMenuList();
    // La hoja 122 se guardo con factory '/VISUALIZARPROY' bajo la raiz '/ODP':
    // tieneAccesoMenu acierta con el factory solo o con padre/hoja, se prueban ambos.
    this.canViewGestion = tieneAccesoMenu(menus, "VISUALIZARPROY") || tieneAccesoMenu(menus, "ODP/VISUALIZARPROY");
    this.canViewSeguimiento = tieneAccesoMenu(menus, "seguimiento") || tieneAccesoMenu(menus, "consultaSeguimientos");
    this.rango = obtenerRangoPeriodo(this.periodoInicial);
    this.consultarDashboard();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultarDashboard();
  }

  onPeriodo(rango: RangoPeriodo): void {
    this.rango = rango;
    this.consultarDashboard();
  }

  consultarDashboard(): void {
    this.cargandoDatos = true;
    this.errorCarga = "";

    forkJoin({
      resumen: this.proyectosDashboardService.resumen(this.rango).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const resumen = res.resumen;
          if (!resumen || this.esError(resumen.pCodRetorno)) {
            this.limpiar();
            this.errorCarga = "No fue posible cargar la informacion de proyectos.";
            this.repintar();
            return;
          }
          this.aplicar(resumen);
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de proyectos:", e);
          this.limpiar();
          // RxJS no llama a complete despues de un error: hay que apagar la
          // carga aqui o la tarjeta "Actualizando indicadores..." se queda.
          this.cargandoDatos = false;
          this.errorCarga = "No fue posible cargar la informacion de proyectos.";
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getDonutStyle(): Record<string, string> {
    if (this.totalProyectos <= 0 || this.estatusItems.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(this.estatusItems, this.totalProyectos);
  }

  getBarPercent(item: DashboardGraphItem, total: number): number {
    return porcentaje(item.valor, total);
  }

  getBarStyle(item: DashboardGraphItem, index: number, total: number): Record<string, string> {
    return {
      width: `${this.getBarPercent(item, total)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  /** Puntos de atraso con signo: "+12" va atrasado, "-3" va adelantado. */
  getDesviacionLabel(item: ProyectoDesviacion): string {
    const desviacion = toNumber(item?.desviacion);
    return `${desviacion > 0 ? "+" : ""}${desviacion} pts`;
  }

  getDesviacionClass(item: ProyectoDesviacion): string {
    const desviacion = toNumber(item?.desviacion);
    if (desviacion > 4) {
      return "proyectos-desviacion--atrasado";
    }
    return desviacion < 0 ? "proyectos-desviacion--adelantado" : "proyectos-desviacion--en-tiempo";
  }

  abrirGestion(): void {
    if (!this.canViewGestion) {
      return;
    }
    this.router.navigate(["/ODP/VISUALIZARPROY"]);
  }

  abrirDetalle(item: ProyectoDesviacion): void {
    if (this.canViewSeguimiento && item?.id) {
      this.router.navigate(["/consultaSeguimientos/detalle", item.id]);
    }
  }

  private aplicar(resumen: ResumenDashboardProyectos): void {
    this.totalProyectos = toNumber(resumen.totalProyectos);
    this.activos = toNumber(resumen.activos);
    this.estatusItems = normalizarGraficas(resumen.porEstatus || []).map((item) => ({
      ...item,
      color: this.statusColors[item.nombre] || item.color,
    }));
    this.clienteItems = normalizarGraficas(resumen.porCliente || []).slice(0, 8);
    this.mesItems = normalizarGraficas(resumen.porMes || [], false).slice(-8);
    this.mesMax = Math.max(...this.mesItems.map((item) => item.valor), 0);
    this.severidadItems = this.normalizarSeveridad(resumen.riesgosPorSeveridad || []);
    this.totalRiesgos = this.severidadItems.reduce((total, item) => total + item.valor, 0);
    this.desviaciones = (resumen.mayorDesviacion || []).slice(0, 5);
    this.kpis = this.construirKpis(resumen);
  }

  private limpiar(): void {
    this.kpis = [];
    this.estatusItems = [];
    this.clienteItems = [];
    this.mesItems = [];
    this.severidadItems = [];
    this.desviaciones = [];
    this.totalProyectos = 0;
    this.activos = 0;
    this.totalRiesgos = 0;
    this.mesMax = 0;
  }

  private esError(pCodRetorno: string | null | undefined): boolean {
    return !!pCodRetorno && pCodRetorno !== "00000";
  }

  private construirKpis(resumen: ResumenDashboardProyectos): DashboardKpi[] {
    const activos = toNumber(resumen.activos);
    const horasReales = toNumber(resumen.horasReales);
    const horasEstimadas = toNumber(resumen.horasEstimadas);

    return [
      {
        label: "Proyectos activos",
        value: activos,
        meta: `${toNumber(resumen.pausados)} pausados / ${toNumber(resumen.cerrados)} cerrados`,
        icon: "account_tree",
        className: "ticket-kpi--total",
      },
      {
        label: "Retrasados",
        value: toNumber(resumen.retrasados),
        meta: `${toNumber(resumen.vencidos)} vencidos de ${activos} activos`,
        icon: "running_with_errors",
        className: "ticket-kpi--active",
      },
      {
        label: "Sin avance reciente",
        value: toNumber(resumen.sinAvanceReciente),
        meta: "Activos sin avance en mas de 7 dias",
        icon: "notifications_paused",
        className: "ticket-kpi--muted",
      },
      {
        label: "Avance real",
        value: `${toNumber(resumen.avanceRealPromedio)}%`,
        meta: `planeado ${toNumber(resumen.avancePlaneadoPromedio)}% (promedio de activos)`,
        icon: "trending_up",
        className: "ticket-kpi--done",
      },
      {
        label: "Horas reales vs estimadas",
        value: `${horasReales} h`,
        // Las horas reales solo suman las tareas cuyo id_proyecto es el id de
        // Mongo: lo anterior a la migracion no cuenta, asi que el porcentaje
        // es "cuanto se registro con el timer", no cobertura total.
        meta: `de ${horasEstimadas} estimadas (${porcentaje(horasReales, horasEstimadas)}% registrado con timer)`,
        icon: "timer",
        className: "ticket-kpi--time",
      },
      {
        label: "Riesgos abiertos",
        value: toNumber(resumen.riesgosAbiertos),
        meta: `en ${activos} proyectos activos`,
        icon: "warning",
        className: "ticket-kpi--active",
      },
    ];
  }

  /** Conserva el orden del backend (de critico a bajo) y colorea por severidad. */
  private normalizarSeveridad(items: Graficas[]): DashboardGraphItem[] {
    return items
      .filter((item) => item && item.valor1 !== null && item.valor1 !== undefined)
      .map((item, index) => {
        const nombre = String(item.valor1 || "Sin severidad").toUpperCase();
        return {
          nombre,
          valor: toNumber(item.valor2),
          color: this.severityColors[nombre] || GRAPH_COLORS[index % GRAPH_COLORS.length],
        };
      });
  }
}
